import type {
  CustomStoreDesign,
  CustomStoreProduct,
  DtpCategoryContentResponse,
} from "@bloom/shared";
import { resolveStorefrontTheme } from "../../../lib/storefrontTheme";
import "./custom-store.css";
import {
  EducationalHero,
  EducationalCategories,
} from "../../dtp/educational/EducationalHomeSections";

export function CustomStoreView({
  design,
  clinicName,
  coverage,
  products,
  assetUrl,
  onProduct,
  categoryContent = [],
  onCategory,
}: {
  design: CustomStoreDesign;
  clinicName: string;
  /** Clinic coverage line (own licenses ∪ telemed network) for the hero badge. */
  coverage?: import("@bloom/shared").StoreCoverage | null;
  products: CustomStoreProduct[];
  assetUrl: (id: string) => string | undefined;
  onProduct?: (id: string) => void;
  categoryContent?: DtpCategoryContentResponse[];
  onCategory?: (slug: string) => void;
}) {
  const featured = design.featuredProductIds.flatMap((id) =>
    products.filter((p) => p._id === id),
  );
  const heroImage = design.hero.imageAssetId
    ? assetUrl(design.hero.imageAssetId)
    : undefined;
  return (
    <div
      className={`custom-store custom-store--${design.template} custom-store--${design.accent} custom-store--${design.font}`}
      style={
        {
          ...resolveStorefrontTheme({
            customAccent: design.accent,
            customFont: design.font,
          }).cssVars,
          containerType: "inline-size",
        } as React.CSSProperties
      }
    >
      {design.sections.map((section) => {
        if (section === "hero" && design.template === "educational")
          return (
            <EducationalHero
              key={section}
              clinic={{
                name: clinicName,
                physicianId: "",
                storefrontVariant: "custom",
                coverage,
                featuredProductId: design.featuredProductIds[0],
              }}
              products={products}
              onProduct={onProduct}
              heading={design.hero.heading}
              introduction={design.hero.body}
              heroImage={heroImage}
            />
          );
        if (section === "categories" && design.template === "educational")
          return (
            <EducationalCategories
              key={section}
              products={products}
              categoryContent={categoryContent}
              onCategory={onCategory}
            />
          );
        if (section === "hero")
          return (
            <section
              key={section}
              className={`custom-hero ${heroImage ? "has-image" : ""}`}
            >
              <div>
                <p className="custom-clinic-name">{clinicName}</p>
                <h1>{design.hero.heading}</h1>
                <p className="custom-intro">{design.hero.body}</p>
                <a className="custom-button" href="#clinic-products">
                  Explore treatments
                </a>
              </div>
              {heroImage && (
                <img src={heroImage} alt="" className="custom-hero-image" />
              )}
            </section>
          );
        if (section === "featured" && featured.length)
          return (
            <section key={section} className="custom-section">
              <h2>Featured treatments</h2>
              <div className="custom-products">
                {featured.map((p) => (
                  <article key={p._id}>
                    <h3>{p.displayName}</h3>
                    {p.description && <p>{p.description}</p>}
                    <div className="custom-product-action">
                      <strong>${p.price.toFixed(2)}</strong>
                      <button
                        type="button"
                        className="custom-button"
                        disabled={!onProduct}
                        onClick={() => onProduct?.(p._id)}
                      >
                        View treatment
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          );
        if (section === "about" && design.about.body)
          return (
            <section key={section} className="custom-section custom-about">
              <h2>{design.about.heading}</h2>
              <p>{design.about.body}</p>
            </section>
          );
        if (section === "faq" && design.faqs.length)
          return (
            <section key={section} className="custom-section custom-faq">
              <h2>Questions about our clinic</h2>
              {design.faqs.map((faq, i) => (
                <details key={i}>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </section>
          );
        return null;
      })}
    </div>
  );
}
